import { useMemo } from "react";
import { createRandom } from "./collageLayout";
import { HeartShape, loveTones } from "./hearts";

const particleCount = { burst: 30, rain: 34, spiral: 30 };

const buildParticles = (variant, origin, viewport, seed) => {
  const random = createRandom(seed);
  const reach = Math.min(viewport.width, viewport.height);
  return Array.from({ length: particleCount[variant] }, (_, index) => {
    const size = 10 + random() * 16;
    const color = loveTones[Math.floor(random() * loveTones.length)];
    if (variant === "rain") {
      return {
        id: index,
        size,
        color,
        left: random() * viewport.width,
        top: -30,
        dx: (random() - 0.5) * 120,
        dy: viewport.height + 60,
        spin: (random() - 0.5) * 540,
        delay: random() * 1.4,
        duration: 2.2 + random() * 1.4,
      };
    }
    if (variant === "spiral") {
      return {
        id: index,
        size,
        color,
        left: origin.x,
        top: origin.y,
        angle: (index / particleCount.spiral) * 360,
        distance: reach * (0.28 + random() * 0.32),
        spin: 160 + random() * 140,
        delay: random() * 0.35,
        duration: 1.7 + random() * 0.8,
      };
    }
    const angle = -Math.PI / 2 + (random() - 0.5) * Math.PI * 1.5;
    const distance = reach * (0.22 + random() * 0.4);
    return {
      id: index,
      size,
      color,
      left: origin.x,
      top: origin.y,
      dx: Math.cos(angle) * distance,
      dy: Math.sin(angle) * distance + reach * 0.12,
      spin: (random() - 0.5) * 300,
      delay: random() * 0.25,
      duration: 1.4 + random() * 0.9,
    };
  });
};

export default function HeartBurst({ variant, origin, viewport, seed }) {
  const particles = useMemo(
    () => buildParticles(variant, origin, viewport, seed),
    [variant, origin, viewport, seed]
  );

  return (
    <div className="heart-burst" aria-hidden="true">
      {particles.map((particle) =>
        variant === "spiral" ? (
          <div
            key={particle.id}
            className="heart-orbit"
            style={{
              left: particle.left,
              top: particle.top,
              "--start-angle": `${particle.angle}deg`,
              "--end-angle": `${particle.angle + particle.spin}deg`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
            }}
          >
            <div
              className="heart-orbit-arm"
              style={{
                width: particle.size,
                height: particle.size,
                color: particle.color,
                "--distance": `${particle.distance}px`,
                animationDelay: `${particle.delay}s`,
                animationDuration: `${particle.duration}s`,
              }}
            >
              <HeartShape />
            </div>
          </div>
        ) : (
          <div
            key={particle.id}
            className={`heart-particle heart-particle--${variant}`}
            style={{
              left: particle.left,
              top: particle.top,
              width: particle.size,
              height: particle.size,
              color: particle.color,
              "--dx": `${particle.dx}px`,
              "--dy": `${particle.dy}px`,
              "--spin": `${particle.spin}deg`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
            }}
          >
            <HeartShape />
          </div>
        )
      )}
    </div>
  );
}
